/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { fixedT, translationKeys } from "../../Internationalisierung/i18nBasis";
import AnyUtil from "../../Utils/AnyUtil";
import ArrayUtil from "../../Utils/ArrayUtil";
import { login } from "../../Utils/auth";
import AppSnackbarItem, { Code, Level } from "./AppSnackbarItem";
import AppSnackbarItemFactory, { createCustomErrorItem, createInternalErrorItem, createNetworkErrorItem, createPdfErrorItem, createSuccessItem } from "./AppSnackbarItemFactory";
import IAppSnackbarActions from "./IAppSnackbarActions";
import { isPathMatching, pathFrom } from "./pathHelpers";


type SetAppSnackbarItems = React.Dispatch<React.SetStateAction<AppSnackbarItem[]>>;

export default function createAppSnackbarActions(setAppSnackbarItems: SetAppSnackbarItems): IAppSnackbarActions {
    const push = (newItems: AppSnackbarItem[]) => {
        setAppSnackbarItems((prevItems) => {
            return prevItems
                .filter(prevItem =>
                    !newItems.some(
                        newItem => AnyUtil.equals(prevItem, newItem, true)))
                .concat(newItems);
        });
    };

    const pushSuccess = (source: string) => {
        setAppSnackbarItems((prevItems) => {
            return [...AnyUtil.deepCopy(prevItems), createSuccessItem(source)];
        });
    };

    const pushInternalError = (source?: string) => {
        setAppSnackbarItems((prevItems) => {
            return [...AnyUtil.deepCopy(prevItems), createInternalErrorItem(source)];
        });
    };

    const pushNetworkError = (source?: string) => {
        setAppSnackbarItems((prevItems) => {
            return [...AnyUtil.deepCopy(prevItems), createNetworkErrorItem(source)];
        });
    };

    const pushPdfError = (source?: string) => {
        setAppSnackbarItems((prevItems) => {
            return [...AnyUtil.deepCopy(prevItems), createPdfErrorItem(source)]
        })
    }

    const pushResponseErrors = async (requestName: string | undefined, response: Response) => {
        if (response.ok) {
            return;
        }

        if (response.status === 400) {
            const newItems = await new AppSnackbarItemFactory()
                .createFromResponse(requestName, response);
            push(newItems);
        } else if (response.status === 401) {
            login();
        } else if (response.status === 403) {
            push([
                createCustomErrorItem("createAppSnackbarActions", fixedT(translationKeys.cloudApolloClientProvider.keineRechte))]);
        } else {
            pushInternalError();
        }
    }

    const pushCustomError = (source: string, message: string, code?: Code) => {
        setAppSnackbarItems((prevItems) => {
            return [...AnyUtil.deepCopy(prevItems), createCustomErrorItem(source, message, code)];
        });
    };

    const clear = () => {
        setAppSnackbarItems([]);
    };

    const byLevel = (level: Level) => (item: AppSnackbarItem) => {
        return item.level === level;
    }

    const byNotLevel = (level: Level) => (item: AppSnackbarItem) => {
        return item.level !== level;
    }

    const clearByLevel = (level: Level) => {
        setAppSnackbarItems((prevItems) => {
            if (ArrayUtil.isEmpty(prevItems)) {
                return prevItems;
            }

            return AnyUtil.deepCopy(prevItems.filter(byNotLevel(level)));
        });
    }

    const clearErrors = clearByLevel.bind(null, "Error");

    const clearSuccessItems = clearByLevel.bind(null, "Success");

    const clearOnRouteChange = () => {
        setAppSnackbarItems(prevItems => {
            if (prevItems.some(byLevel("Success"))) {
                return prevItems;
            }

            return [];
        })
    };

    const clearByFieldPath = (typBezeichnung: string, feldPfad: string | string[]) => {
        return clearByPath(pathFrom(typBezeichnung, feldPfad));
    }

    const byNotPath = (path: string[]) => (item: AppSnackbarItem) => {
        return !isPathMatching(item, path);
    };

    const clearByPath = (path: string[]) => {
        setAppSnackbarItems((prevItems) => {
            if (ArrayUtil.isEmpty(prevItems)) {
                return prevItems;
            }

            return AnyUtil.deepCopy(prevItems.filter(byNotPath(path)));
        });
    };

    const clearAndKeepSuccess = () => {
        setAppSnackbarItems((prevItems) => {
            if (ArrayUtil.isEmpty(prevItems)) {
                return prevItems;
            }

            return AnyUtil.deepCopy(prevItems.filter(byLevel("Success")));
        });
    };

    return {
        clear,
        clearAndKeepSuccess,
        clearByFieldPath,
        clearByPath,
        clearErrors,
        clearOnRouteChange,
        clearSuccessItems,
        push,
        pushInternalError,
        pushNetworkError,
        pushPdfError,
        pushResponseErrors,
        pushSuccess,
        pushCustomError,
    };
}